import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { Copy, Check } from 'lucide-react';
import { Invoice } from '@/types/invoice';

interface InvoiceQRModalProps {
  invoice: Invoice | null;
  isOpen: boolean;
  onClose: () => void;
}

type PaymentTab = 'onchain' | 'lightning';

export function InvoiceQRModal({ invoice, isOpen, onClose }: InvoiceQRModalProps) {
  const [activeTab, setActiveTab] = useState<PaymentTab>('onchain');
  const [onchainQRCode, setOnchainQRCode] = useState<string>('');
  const [lightningQRCode, setLightningQRCode] = useState<string>('');
  const [copiedOnchain, setCopiedOnchain] = useState(false);
  const [copiedLightning, setCopiedLightning] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');

  // Check if Lightning invoice is available
  const hasLightning = invoice?.lightningInvoice && (invoice.paymentMethod === 'both' || invoice.paymentMethod === 'lightning');
  const hasOnchain = invoice?.bitcoinAddress || invoice?.payToAddress;

  useEffect(() => {
    if (invoice && isOpen) {
      // Create shareable URL with invoice data
      const invoiceData = {
        id: invoice.id,
        clientName: invoice.clientName,
        amount: invoice.amount,
        currency: invoice.currency,
        details: invoice.details,
        wallet: invoice.wallet,
        createdAt: invoice.createdAt
      };
      
      const encodedData = encodeURIComponent(JSON.stringify(invoiceData));
      const url = `${window.location.origin}/invoice/${invoice.id}?data=${encodedData}`;
      setShareUrl(url);

      // Generate QR code for on-chain Bitcoin address
      if (hasOnchain) {
        const address = invoice.payToAddress || invoice.bitcoinAddress || invoice.wallet;
        QRCode.toDataURL(address, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        }).then(setOnchainQRCode).catch(() => {
          console.error('Failed to generate on-chain QR code');
        });
      }

      // Generate QR code for Lightning invoice
      if (hasLightning && invoice.lightningInvoice) {
        QRCode.toDataURL(invoice.lightningInvoice, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        }).then(setLightningQRCode).catch(() => {
          console.error('Failed to generate Lightning QR code');
        });
      }

      // Set default tab based on available payment methods
      if (hasLightning && !hasOnchain) {
        setActiveTab('lightning');
      } else {
        setActiveTab('onchain');
      }
    }
  }, [invoice, isOpen, hasOnchain, hasLightning]);

  const handleCopyOnchain = async () => {
    if (!invoice) return;
    const address = invoice.payToAddress || invoice.bitcoinAddress || invoice.wallet;
    try {
      await navigator.clipboard.writeText(address);
      setCopiedOnchain(true);
      toast.success('Bitcoin address copied!');
      setTimeout(() => setCopiedOnchain(false), 2000);
    } catch {
      toast.error('Failed to copy address');
    }
  };

  const handleCopyLightning = async () => {
    if (!invoice?.lightningInvoice) return;
    try {
      await navigator.clipboard.writeText(invoice.lightningInvoice);
      setCopiedLightning(true);
      toast.success('Lightning invoice copied!');
      setTimeout(() => setCopiedLightning(false), 2000);
    } catch {
      toast.error('Failed to copy Lightning invoice');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Invoice link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDownloadQR = () => {
    const qrCode = activeTab === 'onchain' ? onchainQRCode : lightningQRCode;
    if (qrCode) {
      const link = document.createElement('a');
      link.download = `invoice-${invoice?.id}-${activeTab}-qr.png`;
      link.href = qrCode;
      link.click();
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#2C2C2E]/90 backdrop-blur-xl p-8 rounded-3xl border border-green-400/20 w-full max-w-md mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all"
        >
          <i className="fa-solid fa-xmark text-white text-sm"></i>
        </button>

        <div className="text-center">
          <h2 className="text-2xl font-bold font-navbar mb-2 text-white">Pay Invoice</h2>
          <p className="text-white/60 mb-4 font-navbar">Choose payment method and scan QR code</p>

          {/* Payment Method Tabs */}
          {(hasOnchain && hasLightning) && (
            <div className="flex gap-2 mb-4 justify-center">
              <button
                onClick={() => setActiveTab('onchain')}
                className={cn(
                  "px-4 py-2 rounded-xl border transition-all font-navbar text-sm",
                  activeTab === 'onchain'
                    ? 'bg-green-500/20 border-green-400/50 text-green-400'
                    : 'bg-[#2C2C2E]/40 border-green-400/20 text-white/60 hover:border-green-400/40'
                )}
              >
                <span className="flex items-center gap-2">
                  <span>₿</span>
                  <span>Bitcoin</span>
                </span>
              </button>
              <button
                onClick={() => setActiveTab('lightning')}
                className={cn(
                  "px-4 py-2 rounded-xl border transition-all font-navbar text-sm",
                  activeTab === 'lightning'
                    ? 'bg-yellow-500/20 border-yellow-400/50 text-yellow-400'
                    : 'bg-[#2C2C2E]/40 border-yellow-400/20 text-white/60 hover:border-yellow-400/40'
                )}
              >
                <span className="flex items-center gap-2">
                  <span>⚡</span>
                  <span>Lightning</span>
                </span>
              </button>
            </div>
          )}

          {/* QR Code */}
          <div className="mb-6 flex justify-center">
            <div className="p-4 bg-white rounded-2xl">
              {activeTab === 'onchain' && onchainQRCode && (
                <img 
                  src={onchainQRCode} 
                  alt="Bitcoin Address QR Code" 
                  className="w-64 h-64"
                />
              )}
              {activeTab === 'lightning' && lightningQRCode && (
                <img 
                  src={lightningQRCode} 
                  alt="Lightning Invoice QR Code" 
                  className="w-64 h-64"
                />
              )}
              {((activeTab === 'onchain' && !onchainQRCode) || (activeTab === 'lightning' && !lightningQRCode)) && (
                <div className="w-64 h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400"></div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details */}
          {activeTab === 'onchain' && hasOnchain && (
            <div className="mb-4 bg-[#2C2C2E]/40 backdrop-blur-xl p-3 rounded-xl border border-green-400/10">
              <p className="text-xs text-white/60 mb-2 font-navbar text-left">Bitcoin Address:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-white/80 font-mono break-all text-left">
                  {(invoice.payToAddress || invoice.bitcoinAddress || invoice.wallet || '').substring(0, 30)}...
                </code>
                <button
                  onClick={handleCopyOnchain}
                  className="flex-shrink-0 p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 border border-green-400/30 transition-colors"
                >
                  {copiedOnchain ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-green-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-green-400/70 mt-2 font-navbar text-left">
                💡 Send Bitcoin to this address. Payment will be confirmed on-chain.
              </p>
            </div>
          )}

          {activeTab === 'lightning' && hasLightning && invoice.lightningInvoice && (
            <div className="mb-4 bg-[#2C2C2E]/40 backdrop-blur-xl p-3 rounded-xl border border-yellow-400/10">
              <p className="text-xs text-white/60 mb-2 font-navbar text-left">Lightning Invoice:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-white/80 font-mono break-all text-left">
                  {invoice.lightningInvoice.substring(0, 30)}...
                </code>
                <button
                  onClick={handleCopyLightning}
                  className="flex-shrink-0 p-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/30 transition-colors"
                >
                  {copiedLightning ? (
                    <Check className="w-4 h-4 text-yellow-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-yellow-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-yellow-400/70 mt-2 font-navbar text-left">
                ⚡ Instant payment with lower fees. Scan with a Lightning wallet.
              </p>
            </div>
          )}

          {/* Invoice Details */}
          <div className="bg-[#2C2C2E]/40 backdrop-blur-xl p-4 rounded-2xl border border-green-400/10 mb-6 text-left">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-white/60 font-navbar">Client:</span>
                <span className="font-medium text-white font-navbar">{invoice.clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 font-navbar">Amount:</span>
                <span className="font-medium text-white font-navbar">{invoice.currency} {invoice.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 font-navbar">MUSD:</span>
                <span className="font-medium text-white font-navbar">{invoice.musdAmount.toFixed(2)} MUSD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 font-navbar">Status:</span>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium font-navbar",
                  invoice.status === 'pending' && "bg-yellow-500/20 text-yellow-400",
                  invoice.status === 'paid' && "bg-green-500/20 text-green-400",
                  invoice.status === 'cancelled' && "bg-red-500/20 text-red-400"
                )}>
                  {invoice.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 font-navbar">Payment Methods:</span>
                <div className="flex gap-1">
                  {hasOnchain && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium font-navbar bg-green-500/20 text-green-400 border border-green-400/30">
                      ₿ Bitcoin
                    </span>
                  )}
                  {hasLightning && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium font-navbar bg-yellow-500/20 text-yellow-400 border border-yellow-400/30">
                      ⚡ Lightning
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCopyLink}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#2C2C2E]/40 hover:bg-green-500/20 transition-colors border border-green-400/20 text-white font-navbar"
            >
              <i className="fa-solid fa-copy"></i>
              Copy Link
            </button>
            <button
              onClick={handleDownloadQR}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-500 hover:bg-green-600 text-white transition-colors font-navbar"
            >
              <i className="fa-solid fa-download"></i>
              Download QR
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
